import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "../../hooks/useApi.js";

/**
 * NoticedPanel — "Noticed by the team" (Getting Good, Nov 2026).
 *
 * Every note a team member shared with their coordinator, flagged ones
 * first, with who and when — and one press per decision:
 *
 *   Add to profile             the note becomes a shift-notes document; the
 *                              profile only changes when Review approves it
 *   Share with the next shift  (when the agency's setting allows)
 *   Keep as a note · Dismiss   (until it's been reviewed)
 *   Remove from the shift log  (while it's on it)
 *
 * The flag is a hint drawn from a keyword list, not a screen: it says what
 * the note mentions and asks the coordinator to judge.
 *
 * "Add to profile" runs an extraction that can take a minute or more, so the
 * row shows where it has got to and the list refreshes while any is adding.
 */
export const NOTICED_COPY = {
  title: "Noticed by the team",
  buttons: {
    add_to_profile: "Add to profile",
    share: "Share with the next shift",
    keep: "Keep as a note",
    dismiss: "Dismiss",
    remove: "Remove from the shift log",
  },
  flag: terms => `Mentions: ${terms.join(", ")}. Check whether this needs an incident report.`,
  progress: (key, poss) => ({
    adding: "Adding to profile…",
    ready: "Ready in Review",
    in_profile: `In ${poss} profile`,
    failed: "Couldn't add. Try again.",
    reviewed: "Reviewed — nothing added",
  })[key] || null,
  shiftNow: "Asked to go to the next shift now",
  live: "With the next shift",
  used: n => `helpful to ${n}`,
  empty: "Nothing new from the team.",
  tabs: { new: "New", kept: "Kept", all: "All shared" },
};

const ACTION_ORDER = ["add_to_profile", "share", "keep", "dismiss", "remove"];
const POLL_MS = 5000;

function relative(iso) {
  if (!iso) return "";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} h ago`;
  const days = Math.floor(hrs / 24);
  return days === 1 ? "yesterday" : `${days} days ago`;
}

export default function NoticedPanel({ clientId, pronouns, onChanged }) {
  const [tab, setTab] = useState("new");
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const { data, refetch } = useApi(`/clients/${clientId}/notes/inbox?status=${tab}`);
  const notes = data?.notes || [];
  const p = (pronouns || "").toLowerCase();
  const poss = p.startsWith("she") ? "her" : p.startsWith("he") ? "his" : "their";
  const adding = notes.some(n => n.progress === "adding");

  useEffect(() => {
    if (!adding) return undefined;
    const t = setInterval(() => refetch?.(), POLL_MS);
    return () => clearInterval(t);
  }, [adding, refetch]);

  async function act(id, action) {
    setBusy(`${id}:${action}`); setError(null);
    try {
      const r = await fetch(`/api/clients/${clientId}/notes/${id}/review`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || "Couldn't do that.");
      refetch?.(); onChanged?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div style={s.wrap}>
      <div style={s.tabs} role="tablist">
        {Object.entries(NOTICED_COPY.tabs).map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k}
                  style={{ ...s.tab, ...(tab === k ? s.tabOn : {}) }} onClick={() => setTab(k)}>{label}</button>
        ))}
      </div>
      {error && <p style={s.error}>{error}</p>}
      {data && notes.length === 0 && <p style={s.muted}>{NOTICED_COPY.empty}</p>}
      <ul style={s.list}>
        {notes.map(n => {
          const progress = NOTICED_COPY.progress(n.progress, poss);
          return (
            <li key={n.id} style={{ ...s.row, ...(n.flagged ? s.rowFlag : {}) }}>
              <div style={s.meta}>
                <strong style={s.author}>{n.author}</strong>
                <span>{relative(n.created_at)}</span>
                {n.to_shift_now && <span>· {NOTICED_COPY.shiftNow}</span>}
                {n.live && <span>· {NOTICED_COPY.live}</span>}
                {n.live && n.use_count > 0 && <span>· {NOTICED_COPY.used(n.use_count)}</span>}
              </div>
              {n.flagged && <p style={s.flag}>{NOTICED_COPY.flag(n.flag_terms)}</p>}
              <p style={s.text}>{n.text}</p>
              {progress && (
                <p style={s.progress}>
                  {n.progress === "ready"
                    ? <Link to={`/coordinator/clients/${clientId}/review`} style={s.link}>{progress} ›</Link>
                    : progress}
                </p>
              )}
              {n.actions?.length > 0 && (
                <div style={s.buttons}>
                  {ACTION_ORDER.filter(a => n.actions.includes(a)).map(a => (
                    <button key={a} type="button" disabled={!!busy}
                            style={{ ...s.btn, ...(a === "add_to_profile" ? s.btnMain : {}) }}
                            onClick={() => act(n.id, a)}>
                      {busy === `${n.id}:${a}` ? "…" : NOTICED_COPY.buttons[a]}
                    </button>
                  ))}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

const s = {
  wrap:     { display: "flex", flexDirection: "column", gap: "var(--space-3)" },
  tabs:     { display: "flex", gap: 6 },
  tab:      { font: "inherit", fontSize: "var(--text-xs)", padding: "4px 10px", borderRadius: 999, border: "1px solid var(--color-border)", background: "var(--color-surface)", color: "var(--color-text-secondary)", cursor: "pointer" },
  tabOn:    { background: "var(--color-accent-subtle)", color: "var(--color-accent)", borderColor: "var(--color-accent)" },
  list:     { listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: "var(--space-2)" },
  row:      { background: "var(--color-bg-base)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "var(--space-3)", display: "flex", flexDirection: "column", gap: 6 },
  rowFlag:  { borderColor: "var(--color-critical)" },
  meta:     { display: "flex", flexWrap: "wrap", gap: 6, fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", alignItems: "baseline" },
  author:   { color: "var(--color-text-primary)", fontWeight: "var(--weight-semibold)" },
  flag:     { margin: 0, fontSize: "var(--text-sm)", color: "var(--color-critical)" },
  text:     { margin: 0, fontFamily: "var(--font-serif)", fontSize: "var(--text-base)", lineHeight: 1.45, color: "var(--color-text-primary)" },
  progress: { margin: 0, fontSize: "var(--text-sm)", color: "var(--color-text-secondary)" },
  link:     { color: "var(--color-accent)", textDecoration: "none" },
  buttons:  { display: "flex", flexWrap: "wrap", gap: 6 },
  btn:      { font: "inherit", fontSize: "var(--text-xs)", padding: "6px 10px", borderRadius: "var(--radius-md)", border: "1px solid var(--color-border-strong)", background: "var(--color-surface)", color: "var(--color-text-primary)", cursor: "pointer" },
  btnMain:  { background: "var(--color-accent)", color: "var(--color-text-on-accent)", borderColor: "var(--color-accent)" },
  muted:    { margin: 0, fontSize: "var(--text-sm)", color: "var(--color-text-tertiary)" },
  error:    { margin: 0, fontSize: "var(--text-sm)", color: "var(--color-critical)" },
};
